import { action, observable } from 'mobx';
import stores from 'cd-eft/models/Stores';
import { DisplayAlignment } from 'cd-eft/components/SurveyEngineering/Maps/SETMapFunctions/loadLayers';
import { preloadExistingSurvey } from 'cd-eft/components/SurveyEngineering/SOSurveys/SOSurveysVM';
import {
  deleteDesignElement,
  getListOfDesignElements,
} from 'cd-eft/components/Design/common/ElementContainer/ElementContainerVM';
import { appConfig } from 'cd-eft/config/AppConfig';
import { DesignElementController } from 'cd-eft/services/eftSetApi';
import {
  addDesignElementJsonForAlignment,
  createAlignmentAOI,
  removeAignmentFromSummary,
} from '../IndependentAlignment/IndependentAlignmentVM';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';

const alignmentStore = stores.AppStore.alignmentDesignStore;
const setStore = stores.AppStore.surveyEngineeringStore;
const designStore = stores.AppStore.designStore;
const libraryStore = stores.AppStore.projectsLibraryStore;
const gisModel = stores.AppStore.surveyEngineeringStore.gisModel;
const currentWkid = appConfig.tempHardCodes.wkid;

export class AlignmentSummaryTableVM {
  @observable props;
  constructor(props: any) {
    this.props = props;
  }

  alignmentTypes = {
    [appConfig.hardCodes.designElementTypeIndependent]: 'Alignment',
    [appConfig.hardCodes.designElementTypeSpredsheet]: 'Spreadsheet Alignment',
    [appConfig.hardCodes.designElementTypeCrossSlope]: 'CrossSlope Alignment',
  };

  elementActions = {
    Copy: { action: this.copyAlignmentHandler },
    Edit: { action: this.editAlignmentHandler },
    Delete: { action: this.deleteDesignElementHandler },
  };

  soAlignmentActions = {
    Copy: { action: this.copyAlignmentHandler, disabled: true },
    Edit: { action: this.editAlignmentHandler },
    Delete: { action: this.deleteDesignElementHandler, disabled: true },
  };

  unsavedAlignmentsActions = {
    Save: { action: this.saveDuplicateHandler },
    Discard: { action: this.deleteDesignElementHandler },
  };

  // warning dialog -->
  @observable warningDialogIsOpen: boolean = false;
  @action.bound toggleWarningDialog() {
    this.warningDialogIsOpen = !this.warningDialogIsOpen;
  }
  @action.bound warningDialogDiscardAction() {
    this.setNewAligment();
    this.toggleWarningDialog();
  }
  @action.bound warningDialogReturnAction() {
    alignmentStore.currentTab = alignmentStore.alignmentObject.alignmentId ? 'Edit' : 'New';
    this.toggleWarningDialog();
  }
  @action.bound warningDialogSaveAction() {
    // save
    this.setNewAligment();
    this.toggleWarningDialog();
  }
  // <-- warning dialog

  @action.bound async getAlignmentsHandler() {
    designStore.designElementTypeId = appConfig.hardCodes.designElementTypeIndependent //To enable ToolBar on the map for Alignment
    // 1. fetch short data and fill array summaryTable
    await getAlignments();
    // 2. add to tracking displayed alignments
    const layersMap = createLayersMap();
    Array.from(layersMap.keys()).forEach((key) => {
      if (key.includes('alignment')) {
        const alignmentId = Number(key.replace('alignment', ''));
        alignmentStore.selectedAlignments.add(alignmentId);
        alignmentStore.displayedAlignments.add(alignmentId);
      }
    });
  }

  @action.bound addNewAlignmentHandler() {
    if (alignmentStore.alignmentObject.alignmentName) {
      this.toggleWarningDialog();
    } else {
      this.setNewAligment();
    }
  }

  setNewAligment() {
    alignmentStore.resetCurrentObject();
    alignmentStore.alignmentObject.alignmentName = 'New Alignment';
    alignmentStore.currentElement.name = 'New Alignment';
    alignmentStore.currentElement.id = '';
    alignmentStore.currentElement.type = appConfig.hardCodes.designElementTypeIndependent; // 5;
    alignmentStore.currentTab = 'New';
  }

  // copy Alignment ->
  @observable alignmentToCopy = { id: '', name: '' };
  @action.bound copyAlignmentHandler(e: any, item: any) {
    this.alignmentToCopy = { id: item.designElementId, name: item.designElementName };
    alignmentStore.alignmentCopyModalOpen = true;
  }

  @action.bound async saveDuplicateHandler(e: any, item: any) {
    // save alignment
    const itemData = alignmentStore.designElementsDataMap.get(item.designElementId);
    const data = JSON.parse(itemData.json);
    const postJSON = {
      ...itemData,
      ...data,
      designElementId: 0,
      surfaceDefId: designStore.currentSurface.surfaceDefId,
      sessionId: designStore.projectsStore.currentSessionId,
    };
    if (postJSON.alignmentXY) delete postJSON.alignmentXY;
    if (postJSON.json) delete postJSON.json;
    await addDesignElementJsonForAlignment(postJSON);
    // generate AOI
    const postJsonAOI = data.alignmentStations;
    await createAlignmentAOI(postJsonAOI);
  }
  // <-- copy Alignment

  // display Alignment -->
  @action.bound displaySelectedAlignments() {
    const layersMap = createLayersMap();
    alignmentStore.designElementsDataArray.forEach((element: any) => {
      displaySingleAlignment(element, layersMap);
    });
    alignmentStore.displayedAlignments = new Set([...alignmentStore.selectedAlignments]);
  }
  // <-- display Alignment

  // delete Alignment -->
  @observable alignmentToDelete: any = {};
  @observable deleteAlignmentModalOpen: boolean = false;
  @action.bound toggleDeleteAlignmentModal() {
    this.deleteAlignmentModalOpen = !this.deleteAlignmentModalOpen;
  }

  @action.bound deleteDesignElementHandler(e, item) {
    e.preventDefault();
    // open modal to get confirmation from user
    this.deleteAlignmentModalOpen = true;
    this.alignmentToDelete = item;
  }

  @action.bound async deleteIndependentAlignment() {
    // if user agrees to delete
    const designElementId = this.alignmentToDelete.designElementId;
    alignmentStore.isLoading = true;
    this.deleteAlignmentModalOpen = false;
    // check if it is unsaved copy
    if (alignmentStore.unsavedAlignments.has(designElementId)) {
      removeAignmentFromSummary(designElementId);
    } else {
      // remove from DB
      const result = await deleteDesignElement(designElementId);
      if (result && result.designElementId) {
        removeAignmentFromSummary(result.designElementId);
      }
    }
    // reset currentObject in Store
    if (alignmentStore.alignmentObject.designElementId === designElementId) {
      alignmentStore.resetCurrentObject();
    }
    alignmentStore.isLoading = false;
  }
  // <-- delete Alignment

  // edit Alignment -->
  @action.bound async editAlignmentHandler(e: any, item: any) {
    alignmentStore.isLoading = true;
    alignmentStore.currentElement = {
      element: 'alignment',
      id: item.designElementId,
      name: item.designElementName,
      type: item.designElementTypeId,
    };
    const itemData = alignmentStore.designElementsDataMap.get(item.designElementId);
    const jsonData = JSON.parse(itemData.json);
    const alignmentData = {
      ...jsonData,
      designElementId: itemData.designElementId,
      designElementName: itemData.designElementName,
      designElementTypeId: itemData.designElementTypeId,
      sessionId: itemData.sessionId,
      surfaceDefId: designStore.currentSurface.surfaceDefId,
    };
    // fixing stationName for alignment duplicates
    alignmentData.alignmentStations.forEach((el) => {
      el.stationName = el.stationName ? Number(el.stationName) : Math.round(el.stationDistance);
      el.z = Number(el.z) ? Number(el.z) : 0;
    });
    if (item.designElementTypeId === appConfig.hardCodes.designElementTypeIndependent) {
      editIndependentAlignment(alignmentData);
    } else {
      await editDependentAlignment(alignmentData);
    }
    alignmentStore.isLoading = false;
  }
}

export const editDependentAlignment = async (alignmentData) => {
  // const surveyTypes = { 16: 2, 17: 3 };
  const surveyType =
    alignmentData.designElementTypeId === appConfig.hardCodes.designElementTypeSpredsheet
      ? appConfig.hardCodes.surveyTypeSpredsheet
      : appConfig.hardCodes.surveyTypeCrossSlope;
  const item = { surveyType: surveyType };
  setStore.currentSurvey = {
    ...setStore.currentSurvey,
    ...alignmentData,
    ...item,
  };
  await preloadExistingSurvey(item);
  alignmentStore.currentTab = 'Edit';
  setStore.currentSubTab = 'alignment';
};

export const editIndependentAlignment = (alignmentData) => {
  alignmentStore.alignmentObject = {
    ...alignmentStore.alignmentObject,
    ...alignmentData,
  };
  alignmentStore.begginingStation = alignmentStore.alignmentObject.beginningStationDis.toString();
  alignmentStore.currentTab = 'Edit';
  alignmentStore.isLoading = false;
  designStore.disableInput = false;
  removeSingleAlignmentLayer(`alignment${alignmentData.designElementId}`);
  if (alignmentData.alignmentStations?.some(station => station.z == 0)) {
    libraryStore.resetZValue = true;
  } else {
    libraryStore.resetZValue = false;
  }
};
// <-- edit Alignment

export const getAlignments = async () => {
  alignmentStore.isLoading = true;
  alignmentStore.summaryTable = [];
  designStore.designElementId = 0;
  const sessionId = designStore.projectsStore.currentSessionId;

  const structureList = 'Alignment,Alignment-Spreadsheet,Alignment-CrossSlope';
  try {
    const response = await DesignElementController.getDesignElementsBySessionAndTypeUsingGET(
      sessionId,
      structureList
    );

    if (response && response.data && response.data.errorResponse) {
      const { errorCode, errorMessage } = response.data.errorResponse;
      if (errorCode === 'Alignment_25') {
        await MessageCenter.addNotificationFromCode(errorCode);
      } else if (errorCode === 'Alignment_26') {
        await MessageCenter.addErrorFromCode('Alignment_26');
      }
    }
    if (response && response.data && response.data.designElementResponses) {
      let data: any = response.data.designElementResponses;
      alignmentStore.summaryTable = [];
    
      //alignment list
      data = data.filter(e => e.designElementTypeId === appConfig.hardCodes.designElementTypeIndependent);
      alignmentStore.summaryTable.push(...data);
    }
  } catch (error) {
    MessageCenter.addError((error as Error).message);
  } finally {
    alignmentStore.isLoading = false;
    if (alignmentStore.summaryTable && alignmentStore.summaryTable.length > 0) {
      await getAlignmentsData();
    }
  }
};

export const getAlignmentsData = async () => {
  alignmentStore.designElementsDataMap = new Map();
  // background fetch full data
  const summaryIds = alignmentStore.summaryTable.map((al) => al.designElementId);
  const designElementsDataArray: any = await getListOfDesignElements(summaryIds.join(','));
  // 2. fill array designElementsDataArray with full data
  alignmentStore.designElementsDataArray = designElementsDataArray;
  // 3. fill Map designElementsDataMap with full data
  alignmentStore.designElementsDataArray.forEach((al) =>
    alignmentStore.designElementsDataMap.set(al.designElementId, al)
  );
};

export const handleCheckboxChange = (e: any) => {
  const designElementId = Number(e.target.value) ? Number(e.target.value) : e.target.value;
  if (e.target.checked) {
    alignmentStore.selectedAlignments.add(designElementId);
  } else {
    if (alignmentStore.selectedAlignments.has(designElementId))
      alignmentStore.selectedAlignments.delete(designElementId);
  }
  if (alignmentStore.selectedAlignments.size === 0) {
    removeAllAlignmentLayers();
  }
};

export const removeAllAlignmentLayers = () => {
  alignmentStore.summaryTable.forEach((al) => {
    removeSingleAlignmentLayer(`alignment${al.designElementId}`);
    // reset currentObject in Store
    if (alignmentStore.alignmentObject.designElementId === al.designElementId) {
      alignmentStore.resetCurrentObject();
    }
  });
};

export const showSingleAlignmentLayer = (ID: any, color: string = '') => {
  if (alignmentStore.designElementsDataMap.has(ID)) {
    const originalJsonData = alignmentStore.designElementsDataMap.get(ID);
    if (alignmentStore.unsavedAlignments.has(ID)) color = 'red';
    if (gisModel.map) {
      if (originalJsonData.json) {
        const jsonData = JSON.parse(originalJsonData.json);
        DisplayAlignment(
          gisModel.map,
          jsonData,
          originalJsonData.designElementName,
          `alignment${ID}`,
          color,
          currentWkid
        );
        alignmentStore.displayedAlignments.add(ID);
      }
    }
    alignmentStore.displayedAlignments.add(ID);
    alignmentStore.selectedAlignments.add(ID);
  }
};

export const removeSingleAlignmentLayer = (layerId: string) => {
  if (gisModel.map) {
    const singleLayerToDelete = gisModel!.map.findLayerById(layerId);
    if (singleLayerToDelete) {
      gisModel.map.remove(singleLayerToDelete);
    }
  }
};

export const displaySingleAlignment = (element: any, layersMap: any) => {
  const isSelected = alignmentStore.selectedAlignments.has(element.designElementId);
  const isDisplayed = layersMap.has(`alignment${element.designElementId}`);
  const isUnsaved = alignmentStore.unsavedAlignments.has(element.designElementId);
  const color = isUnsaved ? 'red' : '';
  if (isSelected && isDisplayed) return;
  if (isSelected && !isDisplayed) {
    if (element.json) {
      const jsonData = JSON.parse(element.json);
      DisplayAlignment(
        gisModel.map,
        jsonData,
        element.designElementName,
        `alignment${element.designElementId}`,
        color,
        currentWkid
      );
      alignmentStore.displayedAlignments.add(element.designElementId);
    }
    return;
  }
  if (!isSelected) removeSingleAlignmentLayer(`alignment${element.designElementId}`);
};

export const createLayersMap = () => {
  const myMap = new Map();
  if (gisModel.map) {
    gisModel.map.layers.forEach((lyr) => myMap.set(lyr.id, lyr));
  }
  return myMap;
};
